import { Drawer, Button, List } from 'antd'
import { FC, memo, useState } from 'react'
import styles from './index.module.scss'
import { useTranslation } from '@/services/i18n'
import { ReactComponent as CloseModalIcon } from '@/assets/icons/closeModal.svg'
import addressApi from '@/services/apis/address'
import { ReceiveAddressGroupItem } from '@/services/apis/address/models'
import PaginationList from '../PaginationList'
import { getAddrTypeName } from '@/utils/address'
import emitter from '@/utils/event'
import { CHANGE_RECEIVE_ADDRESS_EVENT } from '@/constants/event'
import classNames from 'classnames'
import { REFRESH_RECEIVE_ADDRESS_EVENT } from '@/constants/event'
import { useGetGroupAddressList } from '@/hooks/address'

interface AddressItemProps extends ReceiveAddressGroupItem {
  addressGroup?: ReceiveAddressGroupItem
  onClick: () => void
}

const AddressItem: FC<AddressItemProps> = ({
  addressGroupKey,
  addressName,
  isDefault,
  addressList,
  addressGroup,
  onClick,
}) => {
  const { t } = useTranslation()
  const cls = classNames({
    [styles.addressGroup]: true,
    [styles.isSelected]: addressGroup?.addressGroupKey === addressGroupKey,
  })
  return (
    <List.Item key={addressGroupKey} className={cls} onClick={onClick}>
      <div className={styles.addressHeader}>
        <div className={styles.addressName}>
          {addressName}
          {isDefault === 1 && <span className={styles.tag}>{t('wallet.address.defaultTag')}</span>}
        </div>
      </div>
      {addressList.map(address => {
        return (
          <div key={address.address} className={styles.addressItem}>
            <div>{getAddrTypeName(address.addressType)}</div>
            <div>{address.address}</div>
          </div>
        )
      })}
    </List.Item>
  )
}

interface ReceiveAddressDrawerProps {
  open: boolean
  walletKey: string
  coinKey: string
  addressGroup?: ReceiveAddressGroupItem
  onClose: () => void
}

const ReceiveAddressDrawer: FC<ReceiveAddressDrawerProps> = ({
  open,
  onClose,
  walletKey,
  coinKey,
  addressGroup,
}) => {
  const { t } = useTranslation()
  const [isCreating, setIsCreating] = useState(false)
  const onCreate = async () => {
    setIsCreating(true)
    const res = await addressApi.createAddress({
      accountKey: walletKey,
      coinKey,
      addressName: 'NewAddress',
    })
    setIsCreating(false)
    if (res.success) {
      window.shMessage.success(t('wallet.address.addAddressSuccess'))
      emitter.emit(REFRESH_RECEIVE_ADDRESS_EVENT)
    }
  }
  const onSelect = (data: ReceiveAddressGroupItem) => {
    emitter.emit(CHANGE_RECEIVE_ADDRESS_EVENT, data)
    onClose()
  }

  const { data, loading, currentPage, setCurrentPage } = useGetGroupAddressList(walletKey, coinKey)

  return (
    <Drawer
      title={t('wallet.address.allAddress')}
      placement="right"
      open={open}
      width={560}
      className={styles.drawer}
      closable={false}
      destroyOnClose={true}
      maskClosable={false}
      extra={
        <div onClick={onClose} className={styles.closeIcon}>
          <CloseModalIcon />
        </div>
      }
      footer={
        <div className={styles.footer}>
          <Button type="primary" onClick={onCreate} loading={isCreating}>
            {t('wallet.address.addAddress')}
          </Button>
        </div>
      }
    >
      <PaginationList
        data={data!}
        loading={loading}
        current={currentPage}
        onChange={setCurrentPage}
        renderItem={item => (
          <AddressItem
            {...(item as ReceiveAddressGroupItem)}
            onClick={() => onSelect(item as ReceiveAddressGroupItem)}
            addressGroup={addressGroup}
          />
        )}
      />
    </Drawer>
  )
}

export default memo(ReceiveAddressDrawer)
